import React, { useEffect, useState } from 'react';
import { db } from '../../../utils/firebase';
import { collection, query, orderBy, getDocs, doc, deleteDoc, addDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { EmailTemplate } from '../../../types';
import { Plus, Edit2, Trash2, Copy, Eye, Search, Filter, ChevronDown, Palette } from 'lucide-react';
import TemplateEditor from './TemplateEditor';
import BrandStyleEditor from './BrandStyleEditor';

const TemplateManager: React.FC = () => {
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [showBrandEditor, setShowBrandEditor] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const q = query(collection(db, 'emailTemplates'), orderBy('updatedAt', 'desc'));
      const snapshot = await getDocs(q);
      const templateList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as EmailTemplate[];
      setTemplates(templateList);
    } catch (error) {
      console.error("Error fetching templates:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    setIsCreating(true);
  };

  const handleEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
    setIsCreating(true);
  };

  const handleDuplicate = async (template: EmailTemplate) => {
    try {
      const newTemplate = {
        name: `${template.name} (Copy)`,
        subject: template.subject,
        htmlContent: template.htmlContent,
        previewText: template.previewText || '',
        category: template.category,
        variables: template.variables,
        isActive: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: 'admin'
      };
      const docRef = await addDoc(collection(db, 'emailTemplates'), newTemplate);
      setTemplates(prev => [{
        ...newTemplate,
        id: docRef.id,
        createdAt: new Date(),
        updatedAt: new Date()
      } as EmailTemplate, ...prev]);
    } catch (error) {
      console.error("Error duplicating template:", error);
      alert("Failed to duplicate template");
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this template? This cannot be undone.')) return;
    try {
      await deleteDoc(doc(db, 'emailTemplates', id));
      setTemplates(prev => prev.filter(t => t.id !== id));
    } catch (error) {
      console.error("Error deleting template:", error);
      alert("Failed to delete template");
    }
  };

  const handleToggleActive = async (template: EmailTemplate) => {
    try {
      await updateDoc(doc(db, 'emailTemplates', template.id), {
        isActive: !template.isActive,
        updatedAt: serverTimestamp()
      });
      setTemplates(prev => prev.map(t =>
        t.id === template.id ? { ...t, isActive: !t.isActive } : t
      ));
    } catch (error) {
      console.error("Error toggling template status:", error);
      alert("Failed to update template status");
    }
  };

  const handleSave = async (templateData: Partial<EmailTemplate>) => {
    try {
      if (editingTemplate) {
        // Update existing
        await updateDoc(doc(db, 'emailTemplates', editingTemplate.id), {
          ...templateData,
          updatedAt: serverTimestamp()
        });
        setTemplates(prev => prev.map(t =>
          t.id === editingTemplate.id ? { ...t, ...templateData, updatedAt: new Date() } : t
        ));
      } else {
        // Create new
        const newTemplate = {
          ...templateData,
          isActive: true,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: 'admin'
        };
        const docRef = await addDoc(collection(db, 'emailTemplates'), newTemplate);
        setTemplates(prev => [{
          ...newTemplate,
          id: docRef.id,
          createdAt: new Date(),
          updatedAt: new Date()
        } as EmailTemplate, ...prev]);
      }
      setIsCreating(false);
      setEditingTemplate(null);
    } catch (error) {
      console.error("Error saving template:", error);
      alert("Failed to save template");
    }
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingTemplate(null);
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.subject.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || template.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'welcome': return 'bg-green-100 text-green-800 border-green-200';
      case 'promotional': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'newsletter': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'transactional': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (date: any) => {
    if (!date) return 'N/A';
    if (date.toDate) return date.toDate().toLocaleDateString();
    return new Date(date).toLocaleDateString();
  };

  if (isCreating) {
    return (
      <TemplateEditor
        template={editingTemplate}
        onSave={handleSave}
        onCancel={handleCancel}
      />
    );
  }

  if (showBrandEditor) {
    return <BrandStyleEditor onClose={() => setShowBrandEditor(false)} />;
  }

  if (loading) {
    return (
      <div className="flex justify-center p-12">
        <div className="animate-spin h-8 w-8 border-4 border-black border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div className="flex flex-wrap gap-4 items-center">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-black font-body text-sm w-64"
            />
          </div>

          {/* Category Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 rounded-sm focus:outline-none focus:border-black font-body text-sm appearance-none bg-white cursor-pointer"
            >
              <option value="all">All Categories</option>
              <option value="welcome">Welcome</option>
              <option value="promotional">Promotional</option>
              <option value="newsletter">Newsletter</option>
              <option value="transactional">Transactional</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={14} />
          </div>
        </div>

        <div className="flex items-center gap-2">
            <button
              onClick={() => setShowBrandEditor(true)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-bold uppercase tracking-wider hover:bg-gray-100 transition-colors"
            >
              <Palette size={16} />
              Brand Style
            </button>
            <button
              onClick={handleCreate}
              className="flex items-center gap-2 px-4 py-2 bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-gray-800 transition-colors"
            >
              <Plus size={16} />
              New Template
            </button>
        </div>
      </div>

      {/* Templates Grid */}
      {filteredTemplates.length === 0 ? (
        <div className="bg-white border border-gray-200 p-12 text-center">
          <p className="text-gray-500 mb-4">
            {templates.length === 0
              ? "No templates yet. Create your first email template."
              : "No templates match your filter."}
          </p>
          {templates.length === 0 && (
            <button
              onClick={handleCreate}
              className="inline-flex items-center gap-2 px-4 py-2 bg-black text-white text-sm font-bold uppercase tracking-wider hover:bg-gray-800 transition-colors"
            >
              <Plus size={16} />
              Create Template
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTemplates.map(template => (
            <div
              key={template.id}
              className="bg-white border border-gray-200 rounded-sm overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Preview Area */}
              <div className="h-40 bg-gray-100 border-b border-gray-200 overflow-hidden relative">
                <div
                  className="transform scale-[0.25] origin-top-left w-[400%] h-[400%] pointer-events-none"
                  dangerouslySetInnerHTML={{ __html: template.htmlContent }}
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-gray-100/80"></div>
              </div>

              {/* Info */}
              <div className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-bold text-sm">{template.name}</h3>
                    <p className="text-xs text-gray-500 truncate">{template.subject}</p>
                  </div>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold border ${getCategoryColor(template.category)} uppercase tracking-wide shrink-0`}>
                    {template.category}
                  </span>
                </div>

                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Updated {formatDate(template.updatedAt)}</span>
                  <button
                    onClick={() => handleToggleActive(template)}
                    className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                      template.isActive
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {template.isActive ? 'Active' : 'Inactive'}
                  </button>
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2 border-t border-gray-100">
                  <button
                    onClick={() => handleEdit(template)}
                    className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-xs font-bold uppercase tracking-wider border border-gray-300 hover:border-black hover:text-black transition-colors"
                  >
                    <Edit2 size={14} />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDuplicate(template)}
                    className="flex items-center justify-center p-2 text-gray-500 hover:text-black border border-gray-300 hover:border-black transition-colors"
                    title="Duplicate"
                  >
                    <Copy size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(template.id)}
                    className="flex items-center justify-center p-2 text-red-500 hover:text-red-700 border border-gray-300 hover:border-red-500 transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer count */}
      <div className="text-sm text-gray-500">
        Showing {filteredTemplates.length} of {templates.length} templates
      </div>
    </div>
  );
};

export default TemplateManager;
